/**
 * SessionListResult component
 * Displays a list of sessions from tool results
 */
import { Link } from 'react-router-dom'
import { MessageSquare, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SessionStatus } from '@/lib/types'

export interface SessionListItem {
  /** Session ID */
  sessionId: string
  /** Current status */
  status: SessionStatus
  /** Number of messages */
  messageCount: number
  /** When the session was created */
  createdAt: string
  /** Goal/task description */
  goal?: string
}

export interface SessionListResultProps {
  /** List of sessions */
  sessions: SessionListItem[]
  /** Whether data is loading */
  isLoading?: boolean
}

const statusColors: Record<string, string> = {
  Completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  Running: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  WaitingForInput: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  Failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Idle: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  Queued: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  Paused: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  Timeout: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  Resumable: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
}

export function SessionListResult({
  sessions,
  isLoading = false,
}: SessionListResultProps) {
  if (isLoading) {
    return (
      <div
        data-testid="session-list-loading"
        className="flex items-center gap-2 text-muted-foreground py-4"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading sessions...</span>
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="text-muted-foreground text-sm py-4">
        No sessions found
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-muted-foreground">
        {sessions.length} session{sessions.length !== 1 ? 's' : ''}
      </div>
      <ul role="list" className="space-y-2">
        {sessions.map((session) => (
          <li
            key={session.sessionId}
            role="listitem"
            className="rounded-md border bg-card p-3"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <Link
                to={`/sessions/${session.sessionId}`}
                className="font-mono text-sm font-medium hover:underline truncate"
              >
                {session.sessionId}
              </Link>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
                  statusColors[session.status] || statusColors.Idle
                )}
              >
                {session.status}
              </span>
            </div>
            {session.goal && (
              <p className="text-sm text-muted-foreground line-clamp-1 mb-1">
                {session.goal}
              </p>
            )}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MessageSquare className="h-3 w-3" />
              <span>{session.messageCount} messages</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
